import { useDefaultCommandCenterControls } from "apollo/components/command-center/hooks";
import {
  useDetailedUsers,
  withTimeout,
} from "apollo/components/members/hooks/useDetailedUsers";
import { getMembershipStatus } from "apollo/components/members/utils/transformUsersForMemberTable";
import { isUUID } from "queries/lib/uuid";
import { searchUsers } from "queries/member/services";
import React, { useMemo } from "react";
import { useQuery } from "react-query";
import { buildFullName } from "ui/components/chat/name-helper";
import { useDebounce } from "ui/hooks/useDebounce";
import { getValidDate } from "ui/lib/helpers";
import { useBootcamps } from "ui/lib/useBootcamps";
import { createContainer } from "unstated-next";

// Past this the search fails with Retry instead of a skeleton that never ends.
export const SEARCH_TIMEOUT_MS = 12000;
const SCAN_DEBOUNCE_MS = 600;

const activityMs = (value) => getValidDate(value)?.getTime?.() || 0;

// Utility function to check if a string is a valid email
const isValidEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

const useContainer = (initial?: { search?: string; teamID?: string }) => {
  const [search, setSearch] = React.useState(initial?.search ?? "");
  const [teamID, setTeamID] = React.useState(initial?.teamID ?? "");
  const { getBootcampById, bootcamps } = useBootcamps();

  const query = search.trim();

  const byName = !!query && !teamID && !isValidEmail(query) && !isUUID(query);
  // The full scan is slow, so it waits for a pause in typing; the indexed
  // search answers each keystroke. A newer term aborts the scan in flight.
  const pausedQuery = useDebounce(query, SCAN_DEBOUNCE_MS);
  const scanTerm = byName ? pausedQuery.trim() : query;

  const filter = useMemo(() => {
    const isEmail = isValidEmail(scanTerm);

    if (isEmail) {
      // If the search input is a valid email, filter by email
      return { email: { eq: scanTerm } };
    }

    const [$givenName = "", $familyName = ""] = scanTerm.split(" ");

    if (isUUID(scanTerm)) {
      // Allow searches for the user by ID
      return { id: { eq: scanTerm } };
    }

    const baseFilter = {
      and: [],
      or: [
        { givenName: { like: $givenName } },
        { familyName: { like: $familyName || $givenName } },
      ],
    };

    if (teamID) {
      // @ts-ignore
      baseFilter.and = [{ teamID: { eq: teamID } }];
    }

    if (!$familyName) {
      return baseFilter;
    }

    return {
      and: [
        { givenName: { like: $givenName } },
        { familyName: { like: $familyName || $givenName } },
        ...baseFilter.and,
      ],
    };
  }, [scanTerm, teamID]);
  // A name with no team also asks Rocky's indexed name search, which answers
  // in a fraction of a second but leaves out coaches and long-dormant
  // members and stops at 25. The detailed_user view has every member but no
  // name index, so a name that matches few of them can scan it past the
  // timeout; its results join when (if) they arrive. Email, member ID and
  // team-scoped searches go to the view alone.

  const nameSearch = useQuery(
    ["memberNameSearch", query],
    () =>
      withTimeout(
        (signal) => searchUsers(query, { signal }),
        SEARCH_TIMEOUT_MS
      ),
    { enabled: byName, retry: false, staleTime: 30_000 }
  );
  const {
    status,
    data: response,
    error,
    isFetching,
    isLoading,
    refetch,
    hasNextPage,
    fetchNextPage,
  } = useDetailedUsers(
    {
      // Nothing typed is the idle state, not a search for everyone.
      // @ts-ignore
      active: !!scanTerm,
      first: 50,
      filter,
      // Sorting the detailed_user view by last activity, or counting its
      // matches, outlasts Rocky's statement timeout for a short name, so the
      // page comes back unsorted and uncounted and is sorted below.
      totalCount: false,
      timeoutMs: SEARCH_TIMEOUT_MS,
      node: {
        id: true,
        createdAt: {
          __aliasFor: "createdTS",
        },
        profileImg: {
          __aliasFor: "profileImgImgixURL",
        },
        teamID: true,
        givenName: true,
        familyName: true,
        email: true, // Ensure email is included in the query
        lastActivityTS: true,
        // membershipStatus: true,
        // trialExpirationTS: true,
        // numWorkoutsCompleted: true,
      },
    },
    [filter]
  );

  // A team that is gone (archived) reads "No team"; no team is onboarding.
  const teamName = React.useCallback(
    (id) => getBootcampById(id)?.name || (id ? "No team" : undefined),
    [getBootcampById]
  );

  const data = useMemo(() => {
    const named = byName
      ? (nameSearch.data?.users || []).map((user) => {
          const fullName = buildFullName(user.givenName, user.familyName);
          return {
            ...user,
            profileImg: user.profileImgImgixURL,
            bootcampName: teamName(user.teamID),
            fullName,
            label: fullName || user.id,
          };
        })
      : [];
    const pages = (response?.pages || []).map((page) =>
      // @ts-ignore
      (page?.detailedUsers?.edges || [])
        .map((n) => {
          const fullName = buildFullName(
            n?.node?.givenName,
            n?.node?.familyName
          );
          return {
            ...n?.node,
            bootcampName: teamName(n?.node?.teamID),
            fullName,
            label: fullName || n?.node?.email || n?.node?.id,
            cursor: n.cursor,
            membershipStatus: getMembershipStatus({
              membershipStatus: n?.node?.membershipStatus,
              trialExpirationTS: n?.node?.trialExpirationTS,
            }),
          };
        })
        // Most recently active first within each page. Later pages go
        // below, so "Show more" never moves rows already on screen.
        .sort(
          (a, b) => activityMs(b.lastActivityTS) - activityMs(a.lastActivityTS)
        )
    );
    const seen = new Set<string>();
    const fresh = (rows) =>
      rows.filter((n) => {
        if (!n?.id || seen.has(n.id)) return false;
        seen.add(n.id);
        return true;
      });
    // Someone whose whole name is what was typed comes first among the
    // first answers.
    const exact = query.toLowerCase();
    const first = fresh([...named, ...(pages[0] || [])]);
    return [
      ...first.filter((n) => n.label?.toLowerCase() === exact),
      ...first.filter((n) => n.label?.toLowerCase() !== exact),
      ...pages.slice(1).flatMap(fresh),
    ];
  }, [byName, nameSearch.data, response, teamName, query]);

  const matchKeys = useMemo(() => {
    if (isValidEmail(query)) return ["email"];
    if (isUUID(query)) return ["id"];
    return ["label"];
  }, [query]);

  const searchingAll =
    byName && (scanTerm !== query || isLoading) && !nameSearch.isLoading;
  // The scan failed or timed out; the indexed results stand alone.
  const partial = byName && status === "error" && !nameSearch.error;

  const props = useDefaultCommandCenterControls({
    data,
    searchValue: search,
    setSearchValue: setSearch,
    matchKeys,
    // The coverage line and the scan's Show more each take a row's height.
    extraRows:
      (searchingAll || partial ? 1 : 0) + (byName && hasNextPage ? 1 : 0),
  });

  return {
    teamID,
    setTeamID,
    bootcamps,
    hasNextPage,
    fetchNextPage,
    isFetching: isFetching || nameSearch.isFetching,
    query,
    // Loading only for a typed search, so idle never shows a skeleton. A
    // name search shows its first answer while the other is still out.
    searching: !!query && isLoading && (!byName || nameSearch.isLoading),
    // An error only when nothing could answer.
    error:
      status === "error" && (!byName || nameSearch.error)
        ? byName
          ? nameSearch.error
          : error
        : null,
    partial,
    searchingAll,
    // Paging the slow scan is a choice, never a scroll side effect.
    pageOnScroll: !byName,
    retry: () => {
      if (byName) nameSearch.refetch();
      refetch();
    },
    ...props,
  };
};

export const {
  useContainer: useCommandCenterMembersSearch,
  Provider: CommandCenterMembersSearchProvider,
} = createContainer(useContainer);
